/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import { RowData } from '../../types';
import { invalidHighscore } from '../../utils';
import { formatTime } from '../../game-engine/maps-utils/helper-functions';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
  return Array.prototype.sort.call(array, comparator);
}

type IAlign = 'left' | 'center' | 'right';

interface HeadCell {
  disablePadding: boolean;
  id: keyof RowData;
  label: string;
  align: IAlign;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'place',
    align: 'center',
    disablePadding: true,
    label: 'Place',
  },
  {
    id: 'address',
    align: 'center',
    disablePadding: true,
    label: 'Address',
  },
  {
    id: 'collection',
    align: 'left',
    disablePadding: false,
    label: 'Collection',
  },
  {
    id: 'nft_id',
    align: 'center',
    disablePadding: false,
    label: 'NFT ID',
  },
  {
    id: 'score',
    align: 'right',
    disablePadding: false,
    label: 'Score',
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof RowData) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, rowCount, onRequestSort } = props;
  const createSortHandler = (property: keyof RowData) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.align}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface ITableHighscores {
  data: RowData[];
  idLobby: number;
}

export const TableHighscores = (props: ITableHighscores) => {
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof RowData>('score');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof RowData) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const displayMessageRow = (message: string) => {
    return (
      <TableRow hover tabIndex={-1} key={'loading'}>
        <TableCell className="second-color" colSpan={5} align="center">
          <span
            css={css`
              font-weight: bold;
            `}
          >
            {message}
          </span>
        </TableCell>
      </TableRow>
    );
  };

  const highscoreEqual = (h1: RowData, h2: RowData) => {
    return (
      h1.place === h2.place &&
      h1.address === h2.address &&
      h1.collection === h2.collection &&
      h1.nft_id === h2.nft_id &&
      h1.score === h2.score
    );
  };

  const areHighscoresLobby = (highscores: RowData[]) => {
    const no_highscores: RowData = invalidHighscore('no highscores');
    return !(highscores.length == 1 && highscoreEqual(highscores[0], no_highscores));
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - props.data.length) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            className="second-color"
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data.length}
            />
            <TableBody>
              {props.data.length === 0
                ? displayMessageRow('Loading...')
                : !areHighscoresLobby(props.data)
                ? displayMessageRow('No Highscores Submitted')
                : null}

              {areHighscoresLobby(props.data)
                ? stableSort(props.data, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow hover tabIndex={-1} key={row.place}>
                          <TableCell component="th" id={labelId} scope="row" className="second-color">
                            {row.place}
                          </TableCell>
                          <TableCell align="center" className="second-color">
                            {row.address}
                          </TableCell>
                          <TableCell align="left" className="second-color">
                            {row.collection}
                          </TableCell>
                          <TableCell align="right" className="second-color">
                            {row.nft_id}
                          </TableCell>
                          <TableCell align="right" className="second-color">
                            {formatTime(row.score / 1000)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                : null}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={5} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[3, 10, 25]}
          component="div"
          count={props.data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          className="second-color"
        />
      </Paper>
      {/* <FormControlLabel control={<Switch checked={dense} onChange={handleChangeDense} />} label="Dense results" /> */}
    </Box>
  );
};
